import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { AnalyticsStore, QueryRoute, RouteStore } from 'bernie-core';
import { ClickTracker } from 'bernie-analytics';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';
import { UIPageStore } from 'stores/ui-page-store';

import { UitkIcon } from 'uitk-react-icons';
import { UitkLink } from 'uitk-react-link';

import { MAX_RATING } from 'components/infosite/property-details/bar-ratings';
import { ReviewsDialogLayer } from './reviews-dialog-layer';
import { ReviewsDisclaimerTrigger } from './reviews-disclaimer-trigger';

const LinkTracked = ClickTracker(Link);

export interface ReviewsProps {
  routeStore?: RouteStore;
  analytics?: AnalyticsStore;
  reviews?: any;
  uiPage?: UIPageStore;
  currentHotel?: CurrentHotelStore;
}

export type LocalizedReviewsProps = ReviewsProps & LocalizedComponentProps;

export interface ReviewsState {
  initialized: boolean;
}

@withLocalization
@inject('routeStore', 'analytics', 'uiPage', 'currentHotel')
@observer
export class Reviews extends React.Component<LocalizedReviewsProps, ReviewsState> {
  private reviewsAction;
  private reviewsWrapper;

  public constructor(props) {
    super(props);

    this.state = {
      initialized: false
    };

    this.onReviewsDialogClosed = this.onReviewsDialogClosed.bind(this);
  }

  public componentDidMount() {
    this.setState({ initialized: true });
  }

  public render() {
    const { currentHotel, reviews, routeStore, l10n } = this.props;
    const { searchCriteria } = currentHotel;
    const { initialized } = this.state;

    const countClassNames = 'uitk-cell uitk-type-right all-cell-shrink';

    const ra = reviewsAction => {
      this.reviewsAction = reviewsAction;
    };
    const rw = reviewsWrapper => {
      this.reviewsWrapper = reviewsWrapper;
    };
    const schema = 'https://schema.org/AggregateRating';

    const verifiedReviewCountMessage = l10n.formatText('hotels.infosite.reviews.aboveTheFold.verifiedReviews.text');
    const reviewDisclaimer = l10n.formatText('hotels.infosite.reviews.aboveTheFold.legal.text');

    return (
      <div className="uitk-grid all-cell-1-2" ref={rw} itemProp="aggregateRating" itemScope={true} itemType={schema}>
        <span className="uitk-type-300 uitk-cell all-t-padding-two all-cell-fill" data-stid="content-hotel-reviewsummary">
          {reviews &&
            reviews.localizedOverallRating &&
            reviews.superlative && (
              <AbacusExperiment name="PWA_HIS_DATELESS_PDP_Reviews_review_above_the_fold">
                <AbacusControl>
                  <React.Fragment>
                    <meta itemProp="ratingValue" content={reviews.localizedOverallRating} />
                    <span className="reviews-summary__rating uitk-type-bold uitk-type-300">
                      <span className="reviews-summary__rating-value">{`${reviews.localizedOverallRating} / ${MAX_RATING}`}</span>
                    </span>
                    <meta itemProp="description" content={reviews.superlative} />
                    <span className="reviews-summary__superlative pwa-theme--grey-700 all-x-padding-one">{reviews.superlative}</span>
                  </React.Fragment>
                </AbacusControl>
                <AbacusVariant bucket={1}>
                  <React.Fragment>
                    <TopRating rating={reviews.localizedOverallRating} />
                    <TopSuperlative
                      superlative={reviews.superlative}
                      verifiedReviewCountMessage={verifiedReviewCountMessage}
                      reviewDisclaimer={reviewDisclaimer}
                    />
                  </React.Fragment>
                </AbacusVariant>
                <AbacusVariant bucket={2}>
                  <React.Fragment>
                    <TopRating rating={reviews.localizedOverallRating} />
                    <TopSuperlative
                      superlative={reviews.superlative}
                      verifiedReviewCountMessage={verifiedReviewCountMessage}
                      reviewDisclaimer={reviewDisclaimer}
                    />
                  </React.Fragment>
                </AbacusVariant>
              </AbacusExperiment>
            )}
        </span>
        {reviews &&
          reviews.localizedCountWithLabel && (
            <>
              {initialized && (
                <div className={countClassNames}>
                  <UitkLink>
                    <LinkTracked
                      linkName="Display detailed reviews"
                      disabled={!initialized || null}
                      rfrr="UGC.ViewAllResults"
                      events="event419=10"
                      innerRef={ra}
                      to={{
                        pathname: routeStore.pathname,
                        search: this.props.routeStore.mergeQueryString(routeStore.search, { pwaDialog: 'reviews' })
                      }}
                    >
                      <meta itemProp="reviewCount" content={reviews.count} />
                      {reviews.localizedCountWithLabel}
                      <UitkIcon name="chevron_right" />
                    </LinkTracked>
                  </UitkLink>
                </div>
              )}
              {!initialized && (
                <div className={countClassNames}>
                  <span className="uitk-link uitk-link-layout-default uitk-type-300 is-disabled">
                    <meta itemProp="reviewCount" content={reviews.count} />
                    {reviews.localizedCountWithLabel}
                  </span>
                </div>
              )}
            </>
          )}
        <QueryRoute query={{ pwaDialog: 'reviews' }}>
          <ReviewsDialogLayer searchCriteria={searchCriteria} onReviewsDialogClosed={this.onReviewsDialogClosed} />
        </QueryRoute>
      </div>
    );
  }

  private onReviewsDialogClosed() {
    // Close the reviews dialog
    this.props.routeStore.goBack();

    if (this.reviewsAction && typeof this.reviewsAction.focus === 'function') {
      this.reviewsAction.focus();

      return;
    }

    this.reviewsWrapper.focus();
  }
}

interface TopRatingModel {
  rating: string;
}

interface TopSuperlativeModel {
  superlative: string;
  verifiedReviewCountMessage: string;
  reviewDisclaimer: string;
}

const TopRating = ({ rating }: TopRatingModel) => (
  <React.Fragment>
    <meta itemProp="ratingValue" content={rating} />
    <span className="uitk-type-bold uitk-type-400">
      <span className="review-above-fold-rating">{`${rating}/${MAX_RATING} `}</span>
    </span>
  </React.Fragment>
);

const TopSuperlative = ({ superlative, verifiedReviewCountMessage, reviewDisclaimer }: TopSuperlativeModel) => (
  <React.Fragment>
    <meta itemProp="description" content={superlative} />
    <span className="uitk-type-bold uitk-type-400 review-above-fold-superlative">{superlative}</span>
    <div>
      <ReviewsDisclaimerTrigger countMessage={verifiedReviewCountMessage} reviewDisclaimer={reviewDisclaimer} />
    </div>
  </React.Fragment>
);
